/**
 * The only central list of features (architecture §3.1). Owner: Platform. Adding a feature = one import + one entry.
 *
 * `FEATURES` is exactly architecture's list; `APP_FEATURE` carries the app's own screens (boot, title, settings —
 * GDD §4.5) and the `ui` save slice (FTUE step, seen ids, coach marks; GDD C11).
 */
import type { FeatureModule, ScreenDef, SliceDef } from '../shared';
import { createSettingsScreen, localSettingsPort, stubScreen, type SettingsPort } from '../ui';
import { cards } from '../cards';
import { combat } from '../combat';
import { creature } from '../creature';
import { debug } from '../debug';
import { explore } from '../explore';
import { meta } from '../meta';
import { titleScreen } from './screens/title';

/**
 * The `ui` save slice (ui-ux: FTUE step, seen ids for badges, coach marks shown; GDD C11). F1 v1 shape; F3 (coach
 * scheduler) and I1 (FTUE) may add fields — bump `version` with a migration when a field changes meaning.
 * `account.localId` (monetization §11.10) lives here until `SaveState` gains an `account` block (F1 contract request);
 * `create` stays pure, so the app fills the random UUID at boot.
 */
export interface UiSave {
  v: 1;
  /** FTUE step id (ui-ux §12: 'species' … 'done'). */
  ftueStep: string | null;
  /** Ids already seen (badges). */
  seen: string[];
  /** Coach marks already shown (GDD §6.1 rule 7). */
  coach: string[];
  /** A deep link stored during an incomplete FTUE (ui-ux §3.4). */
  pendingDeepLink: string | null;
  account: { localId: string | null };
  [k: string]: unknown;
}
const isStrArray = (x: unknown): boolean => Array.isArray(x) && x.every((s) => typeof s === 'string');
export const uiSlice: SliceDef<UiSave> = {
  key: 'ui',
  version: 1,
  create: () => ({ v: 1, ftueStep: 'species', seen: [], coach: [], pendingDeepLink: null, account: { localId: null } }),
  migrations: {},
  validate: (x) => {
    if (typeof x !== 'object' || x === null) return ['ui: not an object'];
    const o = x as Partial<UiSave>;
    const out: string[] = [];
    if (o.v !== 1) out.push('ui: v must be 1');
    if (o.ftueStep !== undefined && o.ftueStep !== null && typeof o.ftueStep !== 'string') out.push('ui: ftueStep');
    if (o.seen !== undefined && !isStrArray(o.seen)) out.push('ui: seen');
    if (o.coach !== undefined && !isStrArray(o.coach)) out.push('ui: coach');
    if (o.account !== undefined && (typeof o.account !== 'object' || o.account === null)) out.push('ui: account');
    return out;
  },
};

/** App screens (GDD §4.5): «Загрузка» (the DOM boot page in index.html; this stub only serves `?screen=app/boot`),
 *  «Титул» (F1 provisional, `screens/title.ts`; I1/ui-ux §6.3 own it), «Настройки» (F3's screen, ui-ux §6.24). */
export const appScreens: ScreenDef[] = [
  stubScreen({ id: 'app/boot', titleKey: 'app.screen.boot', needs3d: false, kind: 'boot' }),
  titleScreen,
  // F3's «Настройки» (ui-ux §6.24) on the app's settings store (`ctx.settingsPort`, see main.ts).
  createSettingsScreen((ctx) => (ctx as unknown as { settingsPort?: SettingsPort }).settingsPort ?? localSettingsPort()),
];

export const APP_FEATURE: FeatureModule = { key: 'app', slices: [uiSlice], screens: appScreens };

/** Architecture §3.1: `FEATURES = [creature, combat, explore, cards, meta, debug]`. */
export const FEATURES: readonly FeatureModule[] = [creature, combat, explore, cards, meta, debug];

/** App + every feature, in boot order. */
export const ALL_FEATURES: readonly FeatureModule[] = [APP_FEATURE, ...FEATURES];

/** Every registered screen (GDD §4.5 is the only screen list). */
export function allScreens(): ScreenDef[] {
  return ALL_FEATURES.flatMap((f) => f.screens ?? []);
}

/** Every save slice, keyed by slice key. */
export function allSlices(): SliceDef<{ v: number }>[] {
  return ALL_FEATURES.flatMap((f) => f.slices ?? []);
}
